import { useState, useRef, useEffect, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth, useUser, useClerk, Show } from "@clerk/react";
import { Plus, Hexagon, Menu, ChevronDown, Sparkles, Wand2, Play, UserCircle, LogOut, X, ImageIcon, Heart, HelpCircle, BookOpen, Trash2 } from "lucide-react";
import { useUserStore } from "@/stores/user-store";
import { PupLogo } from "@/components/ui/pup-logo";
import { AuthModal } from "@/components/ui/auth-modal";
import { DeleteAccountModal } from "@/components/ui/delete-account-modal";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { navItems, creationNavItems, utilityNavItems } from "@/config/navigation";

const DESKTOP_NAV = [
  { href: "/", label: "Home" },
  { href: "/explore", label: "Explore" },
  { href: "/academy", label: "Academy" },
  { href: "/assets", label: "Assets" },
];

interface TopBarProps {
  onMenuClick?: () => void;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;
      setMobileMenuOpen(false);
    }
  }, [location.pathname]);

  const toggleMenu = useCallback(() => setMobileMenuOpen((v) => !v), []);

  return (
    <div className="relative">
      <header className="flex items-center justify-between px-4 md:px-6 h-14 border-b border-white/6 bg-[#1a1520]/80 backdrop-blur-xl">
        <div className="flex items-center gap-1 md:gap-5">
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 -ml-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>

          <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity mr-1 md:mr-2">
            <PupLogo className="size-7" />
            <span className="text-[15px] font-extrabold tracking-tight text-foreground">
              AnimePup
            </span>
          </Link>

          <nav className="items-center gap-0.5 hidden md:flex">
            {DESKTOP_NAV.map((item) => (
              <DesktopNavLink key={item.href} href={item.href} label={item.label} />
            ))}
            <CreateHoverDropdown />
          </nav>
        </div>

        <Actions />
      </header>

      <MobileDropdown isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
    </div>
  );
}

function MobileDropdown({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const location = useLocation();
  const { credits } = useUserStore();
  const navRef = useRef<HTMLElement>(null);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
      const timer = setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const el = navRef.current;
    if (!el || !mounted || closing) return;

    const checkScroll = () => {
      setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 2);
    };

    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    return () => el.removeEventListener("scroll", checkScroll);
  }, [mounted, closing]);

  if (!mounted) return null;

  const allItems = [
    ...navItems.map((item) => ({ ...item, section: "nav" as const })),
    { section: "divider-create" as const },
    ...creationNavItems.map((item) => ({ ...item, section: "create" as const })),
    { section: "divider-account" as const },
    ...utilityNavItems.map((item) => ({ ...item, section: "account" as const })),
  ];

  let itemIndex = 0;

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 backdrop-blur-sm",
          closing ? "animate-mobile-backdrop-out" : "animate-mobile-backdrop-in",
        )}
        onClick={onClose}
      />
      <div
        className={cn(
          "absolute left-2 right-2 top-full z-50 mt-1.5 bg-[#231c2e]/98 backdrop-blur-xl border border-white/8 rounded-2xl shadow-xl shadow-black/30 overflow-hidden",
          closing ? "animate-mobile-menu-out" : "animate-mobile-menu-in",
        )}
      >
        <nav ref={navRef} className="p-3 max-h-[70vh] overflow-y-auto">
          {allItems.map((entry, i) => {
            if ("section" in entry && typeof entry.section === "string" && entry.section.startsWith("divider")) {
              const label = entry.section === "divider-create" ? "Create" : "Account";
              return (
                <div key={i}>
                  <div className="h-px bg-white/6 mx-2 my-2" />
                  <p className="px-3 pb-1 text-[9px] font-bold text-[#d4a574]/40 uppercase tracking-widest">{label}</p>
                </div>
              );
            }

            if (!("href" in entry)) return null;
            const item = entry as (typeof navItems)[0] & { section: string };
            const Icon = item.icon;
            const isActive = item.href === "/" ? location.pathname === "/" : location.pathname.startsWith(item.href);
            const delay = itemIndex * 50;
            itemIndex++;

            const label = item.href === "/billing" && credits <= 0 ? "Buy Gizmos" : item.label;

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold transition-colors",
                  isActive ? "text-[#d4a574] bg-white/8" : "text-muted-foreground active:bg-white/5",
                  !closing && "animate-mobile-nav-item",
                )}
                style={!closing ? { animationDelay: `${delay}ms` } : undefined}
              >
                <Icon className="size-4" strokeWidth={1.8} />
                {label}
              </Link>
            );
          })}
        </nav>
        <div
          className={cn(
            "pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#231c2e]/90 to-transparent rounded-b-2xl transition-opacity duration-200",
            canScrollDown ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
    </>
  );
}

function DesktopNavLink({ href, label }: { href: string; label: string }) {
  const location = useLocation();
  const isActive = href === "/"
    ? location.pathname === "/"
    : location.pathname.startsWith(href);

  return (
    <NavLink
      to={href}
      end={href === "/"}
      className={cn(
        "px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all",
        isActive
          ? "text-[#d4a574] bg-white/10"
          : "text-muted-foreground hover:text-foreground hover:bg-white/5",
      )}
    >
      {label}
    </NavLink>
  );
}

const CREATE_HOVER_ITEMS = [
  { href: "/create-character", label: "Create Character", icon: UserCircle },
  { href: "/animate-character", label: "Animate Character", icon: Play },
];

function CreateHoverDropdown() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isCreateActive = ["/create-character", "/animate-character"].some((path) => location.pathname.startsWith(path));

  const handleEnter = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div
      className="relative"
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <Link
        to="/create-character"
        className={cn(
          "flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all",
          isCreateActive
            ? "text-[#d4a574] bg-white/10"
            : "text-muted-foreground hover:text-foreground hover:bg-white/5",
        )}
      >
        Create
        <ChevronDown className={cn("size-3 transition-transform duration-200", open && "rotate-180")} />
      </Link>

      {open && (
        <div className="absolute top-full left-0 pt-1.5 z-50">
          <div className="w-52 rounded-2xl bg-[#231c2e] border border-white/10 shadow-lg shadow-black/30 p-1.5 animate-modal-in">
            {CREATE_HOVER_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[12px] font-semibold text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
                >
                  <Icon className="size-3.5 text-[#d4a574]/60" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Actions() {
  const { isLoaded: isClerkLoaded } = useAuth();
  const { credits, isLoading: isUserLoading } = useUserStore();
  const [showAuth, setShowAuth] = useState(false);

  if (!isClerkLoaded) {
    return <div className="h-8 w-24 rounded-full bg-amber-100/50 animate-pulse" />;
  }

  return (
    <div className="flex items-center gap-2">
      <Show when="signed-out">
        <button
          onClick={() => setShowAuth(true)}
          className="btn-primary text-[12px] py-1.5 px-4 rounded-full"
        >
          Get Started
        </button>
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      </Show>
      <Show when="signed-in">
        <GizmosCounter credits={credits} isLoading={isUserLoading} />
        <Link to="/create-character" className="hidden sm:block">
          <button className="btn-primary flex items-center gap-1.5 text-[12px] py-1.5 px-3.5 rounded-full">
            <Plus className="size-3.5" strokeWidth={2.5} />
            Create
          </button>
        </Link>
        <ProfileButton />
      </Show>
    </div>
  );
}

function GizmosCounter({ credits, isLoading }: { credits: number; isLoading: boolean }) {
  if (isLoading) {
    return <div className="h-8 w-24 rounded-full bg-amber-100/50 animate-pulse" />;
  }

  return (
    <div className="flex items-center rounded-full bg-white/6 border border-white/10 text-[12px] transition-all">
      <Link
        to="/billing"
        className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 group"
      >
        <Hexagon className="size-3.5 text-[#d4a574] fill-[#d4a574]/15 group-hover:fill-[#d4a574]/25 transition-colors" />
        <span className="font-bold text-foreground tabular-nums">
          {credits.toLocaleString()}
        </span>
      </Link>
      <div className="w-px h-4 bg-white/10" />
      <Link
        to="/billing"
        className="pl-2 pr-3 py-1.5 font-semibold text-[#d4a574] hover:text-[#e0b584] transition-colors"
      >
        Buy
      </Link>
    </div>
  );
}

function ProfileButton() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleDeleteAccount = async () => {
    if (!user?.id) return;
    setIsDeleting(true);
    try {
      await api.delete(`/users/${user.id}`);
      await signOut();
    } catch {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(!open)}
          className="relative size-8 rounded-full ring-2 ring-white/15 hover:ring-[#d4a574]/40 transition-all overflow-hidden"
        >
          <img src={user?.imageUrl} alt={user?.fullName || "Profile"} className="size-full object-cover" />
        </button>

        {open && (
          <div className="absolute top-full right-0 mt-1.5 w-48 rounded-2xl bg-[#231c2e] border border-white/10 shadow-lg shadow-black/30 p-1.5 animate-modal-in z-50">
            <div className="px-3 py-2 border-b border-white/6 mb-1">
              <p className="text-[11px] font-bold text-foreground truncate">{user?.fullName || user?.username}</p>
              <p className="text-[9px] text-muted-foreground truncate">{user?.primaryEmailAddress?.emailAddress}</p>
            </div>
            <Link to="/assets" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-[12px] font-semibold text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
              <ImageIcon className="size-3.5" /> Assets
            </Link>
            <Link to="/billing" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-[12px] font-semibold text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
              <Hexagon className="size-3.5" /> Gizmos
            </Link>
            <Link to="/affiliates" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-[12px] font-semibold text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
              <Heart className="size-3.5" /> Affiliate
            </Link>
            <Link to="/support" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-[12px] font-semibold text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
              <HelpCircle className="size-3.5" /> Support
            </Link>
            <div className="h-px bg-white/6 mx-2 my-1" />
            <button onClick={() => { setOpen(false); signOut(); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-[12px] font-semibold text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
              <LogOut className="size-3.5" /> Sign out
            </button>
            <button onClick={() => { setOpen(false); setShowDeleteModal(true); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-[12px] font-semibold text-red-400 hover:bg-red-400/10 transition-colors">
              <Trash2 className="size-3.5" /> Delete Account
            </button>
          </div>
        )}
      </div>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={isDeleting}
      />
    </>
  );
}
